import { PatientCaseWithTimetoken } from 'apps/wrapper/src/hooks/use-leads-management/use-leads-management';
import { FC, useCallback } from 'react';
import { useCommsChat } from '../../../../hooks/use-communications';
import { CallHistoryItem } from '../../../../hooks/use-communications/types/comms-call-types';
import { ManagedCommItem } from '../../../../hooks/use-communications/types/managed-communication';
import { usePubnub } from '../../../../hooks/use-pubnub/use-pubnub';
import { Message } from '../../../../services/pubnub-service';
import CommunicationsCallCard from './communcations-call-card';
import CommunicationsMessageCard from './communcations-message-card';
import { CommunicationAiCard } from './communication-ai-card';
import { ManagedCommCardFactory } from './managed-commnication/managed-comm-card-factory';
import { isAiCase } from './utils/is-ai-case';
import { isCallHistory } from './utils/is-call-history';
import { isManagedComm } from './utils/is-managed-comm';
import { isMessage } from './utils/is-message';

export interface CommunicationCardFactoryProps {
  item: Message | CallHistoryItem | ManagedCommItem | PatientCaseWithTimetoken;
}

export const CommunicationCardFactory: FC<CommunicationCardFactoryProps> = (props) => {
  const { item } = props;
  const { mailboxes } = usePubnub();
  const { sendMessageToServer } = useCommsChat();

  const resend = useCallback(
    (message: Message, mailboxId?: number) => {
      if (mailboxId) {
        const now = new Date();
        const newMessage: Message = {
          ...message,
          date: now.toISOString(),
          timetoken: now.getTime(),
          status: 'sending'
        };
        sendMessageToServer(newMessage, mailboxId);
      }
    },
    [sendMessageToServer]
  );

  if (isAiCase(item)) {
    return <CommunicationAiCard communication={item} />;
  }

  if (isMessage(item)) {
    const curMailbox = mailboxes.find((d) => d.pubnubPrefix === item.channelId.split('.')[0]);
    const locationName = curMailbox ? curMailbox.location.name : '';
    const images = item.media?.map(({ link }) => link) || [];
    return (
      <CommunicationsMessageCard
        sender={
          item.messageType === 'direct'
            ? 'me'
            : item.messageType === 'incoming'
            ? 'patient'
            : item.messageType === 'campaign'
            ? 'system'
            : 'system'
        }
        className={`communications-message-card ${item.messageType}`}
        from={item.from}
        text={item.body}
        timeStamp={item.date}
        senderName={item.senderUserName || ''}
        to={item.to}
        locationName={locationName}
        timestamp={item.date}
        status={item.status}
        images={images}
        phoneNumber={item.channelId.split('.')[1]}
        reaction={item.reaction}
        timetokenString={item.timetokenString}
        onClickResend={() => resend(item, curMailbox?.id)}
      />
    );
  }

  if (isCallHistory(item)) {
    const curMailbox = mailboxes.find((m) => m.locationId === item.locationId);
    const locationName = curMailbox ? curMailbox.location.name : '';
    const patientPhoneNumber = item.callDirection === 'Inbound' ? item.callerNumber : item.calleeNumber;
    let sanitizedNumber = patientPhoneNumber && patientPhoneNumber.replace(/[^\d]/g, '');

    if (sanitizedNumber && sanitizedNumber.length > 10 && sanitizedNumber[0] === '1') {
      sanitizedNumber = sanitizedNumber.substring(1);
    }

    return (
      <CommunicationsCallCard
        caller={item.callDirection === 'Inbound' ? 'patient' : 'me'}
        callType={item.callType}
        duration={item.duration}
        locationName={locationName}
        timestamp={item.callStarted}
        locationId={item.locationId}
        phoneNumber={sanitizedNumber}
        callId={String(item.id)}
        faxTransferredPages={item.faxTransferredPages}
      />
    );
  }

  if (isManagedComm(item)) return <ManagedCommCardFactory communication={item} />;

  return null;
};
